import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, LayoutChangeEvent, ViewProps } from 'react-native';
import {
  PromoBanner,
  PromoBannerBackground,
  PromoBannerText,
  PromoContainer,
  ShieldIcon,
  MoneyIcon,
} from './styles';
import { PromoMarqueeModal } from '../../../shared/PromoMarqueeModal';

import { useNavigation } from '@react-navigation/native';

import shieldIcon from '../../../../assets/images/icons/shield.webp';
import moneyIcon from '../../../../assets/images/icons/moneyBag.webp';

interface PromoMarqueeProps {
  minimumDuration?: number;
  speedMultiplier?: number;
  data: {
    id: number
    text: string
    icon: string
    action: () => void | undefined
  }[]
}

const DEFAULT_MIN_DURATION = 6000;
const DEFAULT_SPEED_MULTIPLIER = 8;

export function PromoMarquee({
  minimumDuration = DEFAULT_MIN_DURATION,
  speedMultiplier = DEFAULT_SPEED_MULTIPLIER,
  data,
}: PromoMarqueeProps) {
  const navigation = useNavigation();

  const marqueeAnim = useRef(new Animated.Value(0)).current;
  const animationRef = useRef<Animated.CompositeAnimation | null>(null);
  const [bannerWidth, setBannerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [ready, setReady] = useState(false);

  const promos = data.map(item => ({
    id: item.id,
    text: item.text,
    icon: item.icon,
    onPress: () => item.action(),
  }));

  const repeatedPromos = Array.from({ length: 4 }).flatMap(() => promos);

  const startAnimation = useCallback(() => {
    if (!bannerWidth || !textWidth) return;

    animationRef.current?.stop();
    marqueeAnim.setValue(0);

    const distance = bannerWidth * 4 + textWidth * 4;
    const duration = Math.max(minimumDuration, distance * speedMultiplier);

    const sequence = Animated.sequence([
      Animated.timing(marqueeAnim, {
        toValue: 1,
        duration,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    ]);

    animationRef.current = Animated.loop(sequence, { resetBeforeIteration: true });
    animationRef.current.start();
    setReady(true);
  }, [bannerWidth, textWidth, marqueeAnim, minimumDuration, speedMultiplier]);

  useEffect(() => {
    startAnimation();
    return () => animationRef.current?.stop();
  }, [startAnimation]);

  const handleBannerLayout = useCallback<NonNullable<ViewProps['onLayout']>>(event => {
    const width = event.nativeEvent.layout.width;
    if (width !== bannerWidth) setBannerWidth(width);
  }, [bannerWidth]);

  const handleTextLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;
    if (width > textWidth) setTextWidth(width);
  }, [textWidth]);

  const translateX = marqueeAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [
      bannerWidth || 0,
      textWidth ? -(textWidth * 4 + (bannerWidth || 0)) : 0,
    ],
  });

  return (
    <>
      <PromoBanner onLayout={handleBannerLayout} activeOpacity={1}>
        <PromoBannerBackground>
          <Animated.View
            style={{
              flexDirection: 'row',
              transform: [{ translateX }],
              opacity: ready ? 1 : 0,
            }}
          >
            {repeatedPromos.map((promo, index) => (
              <PromoContainer
                key={`promo-${index}`}
                onPress={promo.onPress}
                activeOpacity={1}
              >
                {promo.icon === shieldIcon ? (
                  <ShieldIcon source={promo.icon} />
                ) : (
                  <MoneyIcon source={promo.icon} />
                )}

                <PromoBannerText onLayout={handleTextLayout}>
                  {promo.text}
                </PromoBannerText>
              </PromoContainer>
            ))}
          </Animated.View>
        </PromoBannerBackground>

  
      </PromoBanner>
    </>
  );
}
